import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/authStore';
import { useErrorStore } from '@/stores/errorStore';
import type { UserRole } from '@/types/user.types';
import { emailProblem } from '@/utils/accountRules';
import { useFieldErrors } from '@/utils/formErrors';

// QA #68: a taken address is the e-mail field's problem; a 400
// VALIDATION_ERROR arrives with fields.email and lands there by itself.
const PROFILE_FIELDS: Record<string, string> = { USER_EXISTS: 'email', EMAIL_EXISTS: 'email' };

// 個人資料頁（QA #2，決策 D1 / Q-14）。之前 Header 的「個人資料」點了沒反應，
// 因為後端沒有自助更新的端點；現在 PUT /auth/profile 只開放電子信箱，
// 帳號名稱、角色、群組仍由管理員在「使用者管理」維護。

const ROLE_LABELS: Record<UserRole, string> = {
  admin: '最高管理員',
  sub_admin: '群組管理員',
  operator: '操作員',
};

export default function Profile() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const storeLogin = useAuthStore((s) => s.login);
  const showSuccess = useErrorStore((s) => s.showSuccess);

  const [email, setEmail] = useState(user?.email ?? '');
  const form = useFieldErrors();
  // Only for problems that belong to no field (server down …).
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(user?.email ?? '');
  }, [user?.email]);

  const dirty = email.trim().toLowerCase() !== (user?.email ?? '').toLowerCase();

  const handleSave = async () => {
    const value = email.trim();
    setError('');
    const problem = emailProblem(value);
    if (problem) {
      form.setField('email', problem);
      return;
    }
    form.clear();
    setSaving(true);
    try {
      const updated = await authService.updateProfile(value);
      if (token) storeLogin(token, updated);
      showSuccess('個人資料已更新');
    } catch (err) {
      setError(form.apply(err, PROFILE_FIELDS, '更新失敗，請稍後再試'));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        個人資料
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        查看帳號資訊並更新電子信箱
      </Typography>

      <Paper sx={{ p: 3, mt: 3, maxWidth: 640 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              帳號
            </Typography>
            <Typography variant="body1">{user.username}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              角色
            </Typography>
            <Chip label={ROLE_LABELS[user.role]} size="small" sx={{ mt: 0.5 }} />
          </Box>

          {user.primary_group && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                主要群組
              </Typography>
              <Typography variant="body1">{user.primary_group.name}</Typography>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="電子信箱"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            {...form.bind('email', '登入驗證碼與告警通知會寄到這個信箱')}
          />

          <Alert severity="info" icon={false}>
            帳號名稱、角色與群組由管理員在「使用者管理」維護；密碼請至「密碼修改」頁變更。
          </Alert>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button startIcon={<LockIcon />} onClick={() => navigate('/password')}>
              修改密碼
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? <CircularProgress size={20} /> : '儲存'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
